import {
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { POLLING_INTERVALS } from '../../constants/polling';
import { useAuth } from '../../contexts/AuthContext';
import useApiCall from '../../hooks/useApiCall';

/**
 * 알림 유형에 따른 이동 경로 반환
 * @param {Object} notification - 알림 객체
 * @returns {string|null} - 이동 경로 또는 null (이동 없음)
 */
export const getNavigationPath = (notification) => {
  const { notification_type, title } = notification;

  if (notification_type === 'analysis') {
    // 포트폴리오 분석 → /accounts (분석 결과는 계좌관리 페이지에서 표시)
    if (title.includes('AI 분석')) {
      return '/accounts';
    }
    // 주간/정기(월간/분기/연간) 분석, 시장 분석, 종목 추천 → /ai-analysis
    if (
      title.includes('주간 분석') ||
      title.includes('월간 분석') ||
      title.includes('분기 분석') ||
      title.includes('연간 분석') ||
      title.includes('정기 분석') ||
      title.includes('시장 분석') ||
      title.includes('종목 추천')
    ) {
      return '/ai-analysis';
    }
    return null;
  }

  // announcement, maintenance, update는 이동 없음
  return null;
};

function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // API hooks
  const notificationsApi = useApiCall({ initialData: [] });
  const actionApi = useApiCall();

  // Extract stable references from hooks
  const executeAction = actionApi.execute;
  const executeNotifications = notificationsApi.execute;
  const setNotificationsData = notificationsApi.setData;

  const fetchUnreadCount = useCallback(() => {
    if (!isAuthenticated) return;

    executeAction(
      (api, { signal }) => api.get('/api/v1/notifications/unread-count', { signal }),
      { abortPrevious: true },
    ).then((result) => {
      if (result.success) {
        setUnreadCount(result.data?.data?.count || 0);
      }
    });
  }, [isAuthenticated, executeAction]);

  const fetchNotifications = useCallback(() => {
    if (!isAuthenticated) return;

    executeNotifications(
      (api, { signal }) => api.get('/api/v1/notifications?limit=10', { signal }),
      { abortPrevious: true },
    ).then((result) => {
      if (result.success) {
        setNotificationsData(result.data?.data?.notifications || []);
      }
    });
  }, [isAuthenticated, executeNotifications, setNotificationsData]);

  const handleMarkAsRead = useCallback(async (notificationId) => {
    const result = await executeAction((api) =>
      api.put(`/api/v1/notifications/${notificationId}/read`),
    );
    if (result.success) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [executeAction, fetchNotifications, fetchUnreadCount]);

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }

    const path = getNavigationPath(notification);
    if (path) {
      setAnchorEl(null);
      navigate(path);
    }
  }, [handleMarkAsRead, navigate]);

  const handleMarkAllAsRead = useCallback(async () => {
    const result = await executeAction((api) =>
      api.put('/api/v1/notifications/read-all'),
    );
    if (result.success) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [executeAction, fetchNotifications, fetchUnreadCount]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchUnreadCount();

    // 30초마다 폴링
    const interval = setInterval(fetchUnreadCount, POLLING_INTERVALS.NOTIFICATIONS);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  const open = Boolean(anchorEl);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      default: return 'default';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={`${unreadCount}개의 읽지 않은 알림`}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 360, maxHeight: 480 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">알림</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              모두 읽음
            </Button>
          )}
        </Box>

        <Divider />

        {notificationsApi.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notificationsApi.data.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              알림이 없습니다
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notificationsApi.data.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                    cursor: getNavigationPath(notification) ? 'pointer' : 'default',
                    '&:hover': {
                      bgcolor: getNavigationPath(notification) ? 'action.selected' : (notification.is_read ? 'transparent' : 'action.hover'),
                    },
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" component="span">
                          {notification.title}
                        </Typography>
                        {notification.priority !== 'normal' && notification.priority !== 'low' && (
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority)}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {notification.content}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {notification.published_at
                            ? new Date(notification.published_at).toLocaleString()
                            : ''}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                {index < notificationsApi.data.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}

export default NotificationBell;
